/* eslint no-restricted-globals: [0, "history"] */
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import type { IBrand } from './components/brand-list/models';
import type { IPromotion, ICategoryProp, ICategory } from './models';

/**
 * Конвертирует дату в миллисекунды
 *
 * @param date строка даты в формате 25.03.2020
 * @returns количество миллисекунд
 */
const getMilliseconds = (date: string) => {
    const dateArray = date
        .split('.')
        .reverse()
        .map(item => Number(item));

    if (dateArray.length === 3) {
        return Date.UTC(...(dateArray as [number, number, number]));
    }

    return 0;
};
// TODO: что то тут уж очень все сложно
export function useCategories(
    allPromotions: IPromotion[],
    allBrands: IBrand[],
    allCategories: ICategoryProp[],
    defaultBrand?: IBrand,
    defaultCategory?: ICategoryProp
): ICategory[] {
    const uniqueCategories = (() => {
        let categories = allPromotions.reduce(
            (
                result: Array<{ brands: Set<string>; value: string }>,
                { brand: brandKey, categories: categoriesKeys }
            ) => {
                categoriesKeys.forEach(category => {
                    let categoryIndex: number | undefined;
                    const brands = result.find(({ value }, index) => {
                        if (value === category) {
                            categoryIndex = index;

                            return true;
                        }

                        return false;
                    })?.brands;

                    if (brands && categoryIndex !== undefined) {
                        // eslint-disable-next-line no-param-reassign
                        result[categoryIndex].brands = brands.add(brandKey);
                    } else {
                        // eslint-disable-next-line no-param-reassign
                        result = [...result, { value: category, brands: new Set([brandKey]) }];
                    }
                });

                return result;
            },
            []
        );
        if (defaultCategory !== undefined) {
            const allUniqueBrands = categories.reduce(
                (brands: Set<string>, category) => new Set([...brands, ...category.brands]),
                new Set<string>()
            );
            categories = [
                {
                    value: defaultCategory.value,
                    brands: allUniqueBrands,
                },
                ...categories,
            ];
        }

        return categories;
    })();

    return uniqueCategories.map(({ brands, value: categoryKey }) => {
        let preparedBrands = Array.from(brands, brandKey =>
            allBrands.find(({ value }) => value === brandKey)
        )
            .reduce((brandsList: IBrand[], brand) => {
                if (brand === undefined || brand.label === undefined) {
                    return brandsList;
                }

                return [...brandsList, brand];
            }, [])
            .sort(({ label: labelA }, { label: labelB }) => {
                const labelA1 = labelA as string;
                const labelB1 = labelB as string;
                if (labelA1 > labelB1) {
                    return 1;
                }
                if (labelA1 < labelB1) {
                    return -1;
                }

                return 0;
            });

        if (defaultBrand !== undefined) {
            preparedBrands = [defaultBrand, ...preparedBrands];
        }

        const { badges = [], ...rest } = allCategories.find(
            ({ value }) => value === categoryKey
        ) || { header: '', label: '', value: '' };

        return {
            ...rest,
            brands: preparedBrands,
            badges: badges.map(
                (value: string) =>
                    preparedBrands.find(({ value: brandValue }) => brandValue === value) || {
                        value,
                    }
            ),
        };
    });
}

export function usePromotions(
    allPromotions: IPromotion[],
    selectedCategoryValue: string,
    selectedBrandValue: string,
    defaultBrand?: IBrand,
    defaultCategory?: ICategoryProp
) {
    const sortedPromotions = allPromotions.sort(
        ({ dateStart: dateStartA }, { dateStart: dateStartB }) => {
            const dateA = getMilliseconds(dateStartA);
            const dateB = getMilliseconds(dateStartB);

            if (dateA < dateB) {
                return 1;
            }
            if (dateA > dateB) {
                return -1;
            }

            return 0;
        }
    );

    return sortedPromotions
        .filter(
            ({ categories: promotionCategories }) =>
                promotionCategories.includes(selectedCategoryValue) ||
                selectedCategoryValue === defaultCategory?.value
        )
        .filter(
            ({ brand: promotionBrand }) =>
                selectedBrandValue === promotionBrand || selectedBrandValue === defaultBrand?.value
        );
}

export function useButtonMore(
    displayedPromotions: IPromotion[],
    promotions: IPromotion[]
): [boolean, Dispatch<SetStateAction<boolean>>] {
    const [isButtonMoreVisible, setIsButtonMoreVisible] = useState(false);

    useEffect(() => {
        setIsButtonMoreVisible(displayedPromotions.length !== promotions.length);
    }, [displayedPromotions.length, promotions.length]);

    return [isButtonMoreVisible, setIsButtonMoreVisible];
}

export const getWindow = () => typeof window !== 'undefined' && window;

export function setQuery(params: { [key: string]: string }) {
    const window = getWindow();
    if (window && 'URLSearchParams' in window) {
        const { pathname, search } = window.location;
        const searchParams = new URLSearchParams(search);

        Object.entries(params).forEach(([key, value]) => searchParams.set(key, value));

        const newRelativePathQuery = `${pathname}?${searchParams.toString()}`;

        history.pushState(null, '', newRelativePathQuery);
    }
}

export function getQuery() {
    const window = getWindow();
    const result: { [key: string]: string } = {};
    if (window && 'URLSearchParams' in window) {
        const searchParams = new URLSearchParams(window.location.search).entries();
        for (const [key, value] of searchParams) {
            result[key] = value;
        }
    }

    return result;
}
